import { AbsoluteLink } from '@/components/AbsoluteLink';
import { Menu, menuClasses, MenuItem } from 'react-pro-sidebar';
import { scrollToTop } from '@/utils/utils';
import { UserIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/buttons/Button';
import { envIsCanada } from '@/constants/constants';
import React from 'react';
import { useIntl } from 'react-intl';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';
import useGetMobileAppDownloadUrl from '@/hooks/useGetMobileAppDownloadUrl';
import useIsHomePage from '@/hooks/useIsHomePage';
import StorageManager from '@/shared/storageManager';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { isRegulatedLocale } from '@/shared/utility';
import { useModal } from '@/features/modal/useModal';
import { MODALS } from '@/features/modal/types';
import { OpenedFrom } from '@/lib/gTagManager';
import { useRegistration } from '@/hooks/useRegistration';

const SidebarFooter = ({ collapsed }: { collapsed: boolean }) => {
  const intl = useIntl();
  const navigate = useAbsoluteNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const locale = useSelector(selectLocale);
  const { openModal } = useModal();
  const { openRegistration } = useRegistration();

  const mobileApps = useGetMobileAppDownloadUrl();
  const isHomeRoute = useIsHomePage();
  const isReturningUser = StorageManager.getIsReturningUser();

  const shouldShowAppDownloadLink =
    !collapsed &&
    !isAuthenticated &&
    mobileApps[0] &&
    mobileApps[0].mobileAppPromotionUrl &&
    !isRegulatedLocale(locale);

  const onAuthenticationClick = () => {
    if (isRegulatedLocale(locale)) {
      openModal(MODALS.PAYNPLAY_IFRAME, { amount: 0, login: true });
      return;
    }

    if (isReturningUser) {
      openModal(MODALS.LOGIN);
      return;
    }

    if (envIsCanada()) {
      openRegistration({ openedFrom: OpenedFrom.ToRemoveFromRegistration });
      return;
    }

    if (isHomeRoute) {
      scrollToTop();
      return;
    }

    navigate('/');
  };

  if (isAuthenticated) return null;

  return (
    <div>
      {shouldShowAppDownloadLink && mobileApps[0].mobileAppPromotionUrl && (
        <div className="flex flex-col gap-y-1 text-sm text-blue-light text-center font-bold px-[16px] hover:text-blue-medium mb-2">
          <AbsoluteLink to={mobileApps[0].mobileAppPromotionUrl}>
            {intl.formatMessage({ id: 'sidebar.app' })}
          </AbsoluteLink>
        </div>
      )}

      <Menu
        rootStyles={{
          ['.' + menuClasses.button]: {
            height: 'auto',
            padding: '8px 20px',
            '&:hover': {
              backgroundColor: collapsed || isAuthenticated ? '#03829a4f' : 'transparent',
              cursor: collapsed ? 'pointer' : 'auto',
            },
          },
        }}
      >
        <MenuItem
          className="mb-[8px]"
          onClick={onAuthenticationClick}
          icon={collapsed && <UserIcon width={30} height={30} />}
        >
          <Button
            text={intl.formatMessage({ id: 'sidebar.enter' })}
            onClickHandler={onAuthenticationClick}
          />
        </MenuItem>
      </Menu>

      {!collapsed && !isAuthenticated && (
        <div className="flex flex-col gap-y-1 text-sm text-center px-[16px] underline my-3">
          <AbsoluteLink className="hover:text-blue-medium" to="/terms-and-conditions">
            {intl.formatMessage({ id: 'footer.termsLinkText' })}
          </AbsoluteLink>
          <AbsoluteLink className="hover:text-blue-medium" to="/privacy-policy">
            {intl.formatMessage({ id: 'footer.privacyPolicyLinkText' })}
          </AbsoluteLink>
        </div>
      )}
    </div>
  );
};

export default SidebarFooter;
